import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { invoiceAPI, usageAPI } from '../api/api';

const PdfViewerWithDetails = ({ open, onClose, invoiceId, usageId, type }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      const loadPdfAndData = async () => {
        try {
          setLoading(true);
          setError(null);

          let response;
          let invoice = null;
          let usage = null;

          if (type === 'invoice') {
            // Load invoice data first
            try {
              const invoiceResponse = await invoiceAPI.getInvoice(invoiceId);
              invoice = invoiceResponse.data;
              setInvoiceData(invoice);
            } catch (invoiceError) {
              console.warn('Invoice data failed to load, but continuing to try PDF loading');
            }

            // Then try to load the PDF
            try {
              response = await invoiceAPI.getInvoicePdf(invoiceId);
              if (response && response.data) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setPdfUrl(url);
              }
            } catch (pdfError) {
              setError(`Failed to load PDF: ${pdfError.message || 'Unknown error'}`);
            }
          } else if (type === 'api-usage') {
            // Load API usage data first
            try {
              const usageResponse = await usageAPI.getApiUsage(usageId);
              usage = usageResponse.data;

              // Get the associated invoice if it exists
              if (usage.invoice_id) {
                const invoiceResponse = await invoiceAPI.getInvoice(usage.invoice_id);
                invoice = invoiceResponse.data;
              }

              setUsageData(usage);
              setInvoiceData(invoice);
            } catch (usageError) {
              console.warn('API usage data failed to load, but continuing to try PDF loading');
            }

            // Then try to load the PDF
            try {
              response = await usageAPI.getApiUsagePdf(usageId);
              if (response && response.data) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setPdfUrl(url);
              }
            } catch (pdfError) {
              setError(`Failed to load PDF: ${pdfError.message || 'Unknown error'}`);
            }
          }
        } catch (err) {
          setError(err.message || 'Failed to load PDF and data');
        } finally {
          setLoading(false);
        }
      };

      loadPdfAndData();
    } else {
      setPdfUrl(null);
      setInvoiceData(null);
      setUsageData(null);
      setError(null);
      setLoading(true);
    }
  }, [open, type, invoiceId, usageId]);

  const renderInvoiceDetails = () => {
    if (!invoiceData) return null;

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>Invoice Details</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.invoice_number || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Vendor</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.vendor?.standard_name || invoiceData.vendor?.name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Amount</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>${invoiceData.total_amount ? parseFloat(invoiceData.total_amount).toFixed(2) : '0.00'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Currency</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.currency || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Invoice Date</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.invoice_date ? new Date(invoiceData.invoice_date).toLocaleDateString() : 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.category?.standard_name || invoiceData.category?.name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.payment_status || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Uploaded By</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{invoiceData.owner?.name || invoiceData.owner?.username || 'N/A'}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderUsageDetails = () => {
    if (!usageData) return null;

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>API Usage Details</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Application Name</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.application_name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Vendor</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.vendor?.standard_name || usageData.vendor?.name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.category?.standard_name || usageData.category?.name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Quantity</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.token_uses || '0'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Unit</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.usage_unit || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.usage_description || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Cost</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>${usageData.cost ? parseFloat(usageData.cost).toFixed(2) : '0.00'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Usage Date</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.usage_date ? new Date(usageData.usage_date).toLocaleDateString() : 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.status || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.invoice?.invoice_number || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Uploaded By</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{usageData.owner?.name || usageData.owner?.username || 'N/A'}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        {type === 'invoice' ? 'Invoice Details' : 'API Usage Details'}
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          p: 0,
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%'
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, width: '100%' }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <>
            {/* PDF Viewer Section - Left Side */}
            <Box
              sx={{
                width: '50%',
                borderRight: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: '#ffffff'
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'auto',
                  p: 2
                }}
              >
                {pdfUrl ? (
                  <iframe 
                    src={pdfUrl} 
                    title="PDF Viewer" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 'none' }} 
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No PDF to show
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Extracted Data Section - Right Side */}
            <Box
              sx={{
                width: '50%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                backgroundColor: '#f5f5f5'
              }}
            >
              {invoiceData && renderInvoiceDetails()}
              {usageData && renderUsageDetails()}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfViewerWithDetails;